import { useRouteLoaderData } from "@remix-run/react";
import { AlertCircle, UserRound } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "~/components/ui/Button";
import { Input } from "~/components/ui/Input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { cn } from "~/lib/utils";
import { useAppStore } from "~/stores/app";
import { GuestData, ReferentData } from "~/stores/bookingCart";
import { useBookingCart as useBookingCartStore } from "~/stores/bookingCartContext";
import { useUser } from "~/stores/user";

interface CheckoutStepProps {
  onCancel: () => void;
  onConfirm: () => void;
  disableConfirm?: boolean;
  actionData?: any;
  isSubmitting?: boolean;
  onRemoveItem?: (item: any, isMainService: boolean) => void;
  loadingRemove?: boolean;
  hasBookingCustomerToken?: boolean;
  isDisabled?: boolean;
  isVoucherBooking?: boolean;
  guestsCount?: { male: number; female: number };
}

export default function CheckoutStep({
  onCancel,
  onConfirm,
  disableConfirm = false,
  actionData,
  isSubmitting = false,
  onRemoveItem,
  loadingRemove = false,
  hasBookingCustomerToken = false,
  isDisabled = false,
  isVoucherBooking = false,
  guestsCount = { male: 0, female: 0 },
}: CheckoutStepProps) {
  const [activeGuestIndex, setActiveGuestIndex] = useState(0);
  const [errors, setErrors] = useState<any>({});
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<any>(null);
  const [isMainServiceToDelete, setIsMainServiceToDelete] = useState(false);
  const [voucherValidationError, setVoucherValidationError] = useState<
    string | null
  >(null);
  const [localZipCode, setLocalZipCode] = useState<string>("");

  const handleDeleteClick = (item: any, isMain: boolean) => {
    setItemToDelete(item);
    setIsMainServiceToDelete(isMain);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = () => {
    if (itemToDelete && onRemoveItem) {
      onRemoveItem(itemToDelete, isMainServiceToDelete);
      setDeleteConfirmOpen(false);
      setItemToDelete(null);
    }
  };

  const { t, i18n } = useTranslation();
  const user = useUser();
  const { bookingCart, setBookingCart } = useBookingCartStore();
  const rootData = useRouteLoaderData<any>("root");
  const { openAuthModal } = useAppStore();

  const referent = useMemo(() => {
    if (bookingCart?.customReferentData) {
      // When user is logged in, always use current profile data for
      // name/surname/email/phone (these aren't editable in this form,
      // and customReferentData may have stale values from before login
      // or from a Zustand hydration race).
      if (user) {
        return {
          ...bookingCart.customReferentData,
          name: user.Name || bookingCart.customReferentData.name || "",
          surname:
            user.Surname || bookingCart.customReferentData.surname || "",
          email:
            user.EmailAddress || bookingCart.customReferentData.email || "",
          phone:
            user.MobileNumber || bookingCart.customReferentData.phone || "",
          zipCode:
            user["Address.ZipCode"] ||
            bookingCart.customReferentData.zipCode ||
            "",
        };
      }
      return bookingCart.customReferentData;
    }
    return {
      name: user?.Name || "",
      surname: user?.Surname || "",
      email: user?.EmailAddress || "",
      phone: user?.MobileNumber || "",
      zipCode: user?.["Address.ZipCode"] || "",
    };
  }, [
    bookingCart?.customReferentData,
    user?.Name,
    user?.Surname,
    user?.EmailAddress,
    user?.MobileNumber,
    user?.["Address.ZipCode"],
  ]);

  const guests = bookingCart?.customGuestsData || [];

  const hasZipCodeAvailable = useMemo(() => {
    const userZipCode = user?.["Address.ZipCode"]?.trim();
    return !!userZipCode;
  }, [user?.["Address.ZipCode"]]);

  const shouldShowZipCodeForGuest1 = !hasZipCodeAvailable && guests.length > 0;

  useEffect(() => {
    if (activeGuestIndex === 0 && shouldShowZipCodeForGuest1) {
      const guestZipCode = guests[0]?.zipCode || "";
      if (guestZipCode !== localZipCode) {
        setLocalZipCode(guestZipCode);
      }
    } else {
      setLocalZipCode("");
    }
  }, [
    activeGuestIndex,
    guests[0]?.zipCode,
    shouldShowZipCodeForGuest1,
    localZipCode,
  ]);

  const summary = useMemo(() => {
    if (
      !bookingCart ||
      !bookingCart.BasketItems ||
      bookingCart.BasketItems.length === 0
    ) {
      return { total: 0, currency: "", totalGuests: 0 };
    }

    const mainItems = bookingCart.BasketItems.filter(
      (item) => item.Role !== "INTEGRATION",
    );

    const total = bookingCart.BasketItems.reduce((acc, item) => {
      if (item.Role === "INCLUDED") {
        return acc;
      }
      return acc + (item.GrossAmount?.Price || 0);
    }, 0);

    const totalGuests = guestsCount.male + guestsCount.female;
    const currency = bookingCart.BasketItems[0].GrossAmount?.Currency || "";

    return { total, currency, totalGuests };
  }, [bookingCart, guestsCount]);

  useEffect(() => {
    if (summary.totalGuests > 0 && guests.length !== summary.totalGuests) {
      const referentData = bookingCart?.customReferentData
        ? bookingCart.customReferentData
        : user
        ? {
            name: user.Name || "",
            surname: user.Surname || "",
            email: user.EmailAddress || "",
            phone: user.MobileNumber || "",
            zipCode: user["Address.ZipCode"] || "",
          }
        : {
            name: "",
            surname: "",
            email: "",
            phone: "",
            zipCode: "",
          };

      const newGuests: any[] = [];

      for (let i = 0; i < guestsCount.male; i++) {
        newGuests.push({
          name: "",
          surname: "",
          email: "",
          isFemale: false,
          zipCode: "",
        });
      }

      for (let i = 0; i < guestsCount.female; i++) {
        newGuests.push({
          name: "",
          surname: "",
          email: "",
          isFemale: true,
          zipCode: "",
        });
      }

      setBookingCart({
        ...bookingCart,
        customGuestsData: newGuests,
        customReferentData: referentData,
      });
    }
  }, [
    summary.totalGuests,
    guests.length,
    setBookingCart,
    bookingCart,
    user,
    guestsCount,
  ]);

  // Prefill: quando l'utente è loggato, precompila il PRIMO ospite con i suoi
  // dati (Nome/Cognome/Email) nello step "Inserisci i dati di ogni ospite".
  // Effetto dedicato e separato dalla creazione delle righe, così funziona
  // anche quando le righe ospite esistono già (carrello ripristinato/persistito)
  // o quando `user` si idrata dopo il primo render. Riempie SOLO se la prima
  // riga è ancora vuota (non sovrascrive ciò che l'utente ha digitato) e si
  // limita a un solo ospite per non violare il controllo anti-duplicati.
  useEffect(() => {
    if (!user) return;
    const currentGuests = bookingCart?.customGuestsData || [];
    const first = currentGuests[0];
    if (!first) return;
    if (first.name || first.surname || first.email) return;
    const newGuests = [...currentGuests];
    newGuests[0] = {
      ...first,
      name: user.Name ?? "",
      surname: user.Surname ?? "",
      email: user.EmailAddress ?? "",
    };
    setBookingCart({ ...bookingCart, customGuestsData: newGuests });
  }, [user, bookingCart, setBookingCart]);

  const handleGuestDataChange = (
    index: number,
    field: string,
    value: string,
  ) => {
    if (isDisabled) {
      openAuthModal("login", "booking");
      return;
    }
    setErrors({ ...errors, guests: null });

    const currentGuests = bookingCart?.customGuestsData || guests;
    const newGuests = [...currentGuests];
    newGuests[index] = { ...newGuests[index], [field]: value };

    const updatedCart: any = {
      ...bookingCart,
      customGuestsData: newGuests,
    };

    if (index === 0 && field === "zipCode") {
      const currentReferent = bookingCart?.customReferentData || referent;
      updatedCart.customReferentData = {
        ...currentReferent,
        zipCode: value,
      };
    }

    setBookingCart(updatedCart);
  };

  // const handleReferentDataChange = (field: string, value: string) => {
  //   if (isDisabled) {
  //     openAuthModal("login", "booking");
  //     return;
  //   }
  //   setErrors({ ...errors, referent: {} });
  //   setBookingCart({
  //     ...bookingCart,
  //     customReferentData: { ...referent, [field]: value },
  //   });
  // };

  const handleInputFocus = () => {
    if (isDisabled) {
      openAuthModal("login", "booking");
    }
  };

  const handleInputClick = (e: React.MouseEvent) => {
    if (isDisabled) {
      e.preventDefault();
      openAuthModal("login", "booking");
    }
  };

  const hasUserData = (guest: any) => {
    if (user) {
      return (
        guest.name === user.Name &&
        guest.surname === user.Surname &&
        guest.email === user.EmailAddress
      );
    }
    if (hasBookingCustomerToken && bookingCart?.customReferentData) {
      return (
        guest.name === bookingCart.customReferentData.name &&
        guest.surname === bookingCart.customReferentData.surname &&
        guest.email === bookingCart.customReferentData.email
      );
    }
    return false;
  };

  const canUseUserData = useMemo(() => {
    if (!user && !hasBookingCustomerToken) return false;
    if (!user && hasBookingCustomerToken && !bookingCart?.customReferentData)
      return false;

    if (user) {
      if (
        !user.Name?.trim() ||
        !user.Surname?.trim() ||
        !user.EmailAddress?.trim()
      ) {
        return false;
      }
    } else if (hasBookingCustomerToken && bookingCart?.customReferentData) {
      const referentData = bookingCart.customReferentData;
      if (
        !referentData.name?.trim() ||
        !referentData.surname?.trim() ||
        !referentData.email?.trim()
      ) {
        return false;
      }
    }

    const currentGuestHasUserData = hasUserData(guests[activeGuestIndex] || {});
    if (currentGuestHasUserData) return false;

    const otherGuestHasUserData = guests.some(
      (guest, index) => index !== activeGuestIndex && hasUserData(guest),
    );
    if (otherGuestHasUserData) return false;

    return true;
  }, [guests, activeGuestIndex, user, hasBookingCustomerToken, bookingCart]);

  const useReferentDataForGuest = () => {
    const newGuests = [...guests];
    if (newGuests[activeGuestIndex]) {
      const dataToUse = user
        ? {
            name: user.Name || "",
            surname: user.Surname || "",
            email: user.EmailAddress || "",
          }
        : bookingCart?.customReferentData
        ? {
            name: bookingCart.customReferentData.name || "",
            surname: bookingCart.customReferentData.surname || "",
            email: bookingCart.customReferentData.email || "",
          }
        : {
            name: referent.name,
            surname: referent.surname,
            email: referent.email,
          };

      newGuests[activeGuestIndex] = {
        ...newGuests[activeGuestIndex],
        ...dataToUse,
      };
      setBookingCart({ ...bookingCart, customGuestsData: newGuests });
    }
  };

  const validateForm = () => {
    const newErrors: any = {
      referent: {},
      guests: [],
    };
    let isValid = true;

    // Validate Referent
    if (!referent.email.trim()) {
      newErrors.referent.email = t("Il campo Email è obbligatorio");
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(referent.email)) {
      newErrors.referent.email = t("Indirizzo Email non valido");
      isValid = false;
    }
    if (!referent.phone.trim()) {
      newErrors.referent.phone = t(
        "Il tuo numero di telefono non è presente nel tuo profilo, modifica il tuo profilo per aggiungerlo",
      );
      isValid = false;
    }
    // if (!referent.zipCode.trim()) {
    //   newErrors.referent.zipCode = t("Il campo CAP è obbligatorio");
    //   isValid = false;
    // }

    const hasOnlyPackages = bookingCart?.BasketItems?.every(
      (item) =>
        item.Role === "MAIN" &&
        (item.ServiceType === "Package" || item.ServiceType === "PACKAGE"),
    );

    if (!hasOnlyPackages) {
      const guestErrors: any[] = Array(guests.length)
        .fill(null)
        .map(() => ({}));
      guests.forEach((guest, index) => {
        let thisGuestIsValid = true;
        const isFemale = guest.isFemale ?? false;
        const sameGenderGuests = guests.filter(
          (g) => (g.isFemale ?? false) === isFemale,
        );
        const genderIndex = sameGenderGuests.findIndex((g) => g === guest) + 1;
        if (!guest.name.trim()) {
          guestErrors[index].name =
            t("Il campo Nome è obbligatorio") +
            t(" per l'ospite {{index}} {{gender}}", {
              index: genderIndex,
              gender: guest.isFemale ? t("Donna") : t("Uomo"),
            });
          isValid = false;
          thisGuestIsValid = false;
        }
        if (!guest.surname.trim()) {
          guestErrors[index].surname =
            t("Il campo Cognome è obbligatorio") +
            t(" per l'ospite {{index}} {{gender}}", {
              index: genderIndex,
              gender: guest.isFemale ? t("Donna") : t("Uomo"),
            });
          isValid = false;
          thisGuestIsValid = false;
        }

        if (!guest.email || !guest.email.trim()) {
          guestErrors[index].email =
            t("Il campo Email è obbligatorio") +
            t(" per l'ospite {{index}} {{gender}}", {
              index: genderIndex,
              gender: guest.isFemale ? t("Donna") : t("Uomo"),
            });
          isValid = false;
          thisGuestIsValid = false;
        }
        if (guest.email && !/\S+@\S+\.\S+/.test(guest.email)) {
          guestErrors[index].email =
            t("Formato Email non valido") +
            t(" per l'ospite {{index}} {{gender}}", {
              index: genderIndex,
              gender: guest.isFemale ? t("Donna") : t("Uomo"),
            });
          isValid = false;
          thisGuestIsValid = false;
        }
        if (index === 0 && shouldShowZipCodeForGuest1) {
          const zipCodeToCheck =
            guest.zipCode?.trim() || referent.zipCode?.trim() || "";
          if (!zipCodeToCheck) {
            guestErrors[index].zipCode = t("Il campo CAP è obbligatorio");
            isValid = false;
            thisGuestIsValid = false;
          }
        }
        if (!thisGuestIsValid && activeGuestIndex !== index) {
          setActiveGuestIndex(index);
        }
      });

      // Controllo ospiti duplicati: almeno uno tra nome, cognome, email deve differire
      for (let i = 0; i < guests.length; i++) {
        for (let j = i + 1; j < guests.length; j++) {
          const a = guests[i];
          const b = guests[j];
          if (
            a.name.trim().toLowerCase() === b.name.trim().toLowerCase() &&
            a.surname.trim().toLowerCase() === b.surname.trim().toLowerCase() &&
            (a.email || "").trim().toLowerCase() ===
              (b.email || "").trim().toLowerCase()
          ) {
            const isFemaleJ = b.isFemale ?? false;
            const sameGenderGuestsJ = guests.filter(
              (g) => (g.isFemale ?? false) === isFemaleJ,
            );
            const genderIndexJ =
              sameGenderGuestsJ.findIndex((g) => g === b) + 1;
            if (!guestErrors[j].duplicate) {
              guestErrors[j].duplicate = t(
                "I dati dell'ospite {{index}} {{gender}} sono identici a quelli di un altro ospite. Almeno uno tra Nome, Cognome o Email deve essere diverso.",
                {
                  index: genderIndexJ,
                  gender: b.isFemale ? t("Donna") : t("Uomo"),
                },
              );
            }
            isValid = false;
          }
        }
      }

      if (guestErrors.some((e) => Object.keys(e).length > 0)) {
        newErrors.guests = guestErrors;
      }
    }
    setErrors(newErrors);
    return isValid;
  };

  function mapCustomDataToRealData() {
    if (bookingCart) {
      // La data di nascita va inviata SOLO sul referente (non sugli ospiti) e
      // proviene dal profilo (signup): utente loggato o dati di registrazione.
      const referentDateOfBirth =
        user?.DateOfBirth || bookingCart.customReferentData?.dateOfBirth || "";

      bookingCart.ReferentData = [
        {
          Code: "NAME",
          Content: referent.name,
        },
        {
          Code: "SURNAME",
          Content: referent.surname,
        },
        {
          Code: "EMAILADDRESS",
          Content: referent.email,
        },
        {
          Code: "MOBILENUMBER",
          Content: referent.phone,
        },
        ...(referentDateOfBirth
          ? ([
              { Code: "DATEOFBIRTH", Content: referentDateOfBirth },
            ] as ReferentData[])
          : []),
        {
          Code: "ADDRESS_ZIPCODE",
          Content: referent.zipCode || "",
        },
        { Code: "ADDRESS_COUNTRY", Content: "IT" },
        {
          Code: "PRIVACYAGREED",
          Content:
            bookingCart.customReferentData?.privacyAgreed !== undefined
              ? bookingCart.customReferentData.privacyAgreed
              : user?.PrivacyAgreed === "1" || true,
        },
        {
          Code: "EMAILCOMMUNICATIONAGREED",
          Content:
            bookingCart.customReferentData?.emailCommunicationAgreed !== undefined
              ? bookingCart.customReferentData.emailCommunicationAgreed
              : user?.EmailCommunicationAgreed === "1" || false,
        },
        {
          Code: "PROFILINGAGREED",
          Content:
            bookingCart.customReferentData?.profilingAgreed !== undefined
              ? bookingCart.customReferentData.profilingAgreed
              : user?.ProfilingAgreed === "1" || false,
        },
        {
          Code: "DATASHARINGAGREED",
          Content:
            bookingCart.customReferentData?.dataSharingAgreed !== undefined
              ? bookingCart.customReferentData.dataSharingAgreed
              : user?.DataSharingAgreed === "1" || false,
        },
      ];

      const mainItems =
        bookingCart.BasketItems?.filter((item) => item.Role === "MAIN") || [];

      const maleLines: number[] = [];
      const femaleLines: number[] = [];

      mainItems.forEach((item) => {
        if (item.Quantity?.Man > 0) {
          for (let i = 0; i < item.Quantity.Man; i++) {
            maleLines.push(item.LineNr);
          }
        }
        if (item.Quantity?.Woman > 0) {
          for (let i = 0; i < item.Quantity.Woman; i++) {
            femaleLines.push(item.LineNr);
          }
        }
      });

      let maleIndex = 0;
      let femaleIndex = 0;

      const mainGuestsData = guests.map((guest, guestIndex) => {
        const guestDataTmp: GuestData[] = [
          {
            Code: "NAME",
            Content: guest.name,
          },
          {
            Code: "SURNAME",
            Content: guest.surname,
          },
          {
            Code: "GENDER",
            Content: guest.isFemale ? "1" : "2",
          },
        ];
        if (guest.email) {
          guestDataTmp.push({
            Code: "EMAILADDRESS",
            Content: guest.email,
          });
        }
        const zipCodeToUse =
          guestIndex === 0
            ? guest.zipCode?.trim() || referent.zipCode?.trim() || ""
            : "";

        if (guestIndex === 0 && zipCodeToUse) {
          guestDataTmp.push({
            Code: "ADDRESS_ZIPCODE",
            Content: zipCodeToUse,
          });
        }

        let refLine = 1;
        if (guest.isFemale && femaleLines.length > 0) {
          refLine = femaleLines[Math.min(femaleIndex, femaleLines.length - 1)];
          femaleIndex++;
        } else if (!guest.isFemale && maleLines.length > 0) {
          refLine = maleLines[Math.min(maleIndex, maleLines.length - 1)];
          maleIndex++;
        } else if (mainItems.length > 0) {
          refLine = mainItems[0].LineNr;
        }

        return {
          PersonalData: guestDataTmp,
          RefLine: refLine,
          Action: "NEW",
        };
      });

      // Assign guests to upselling (INTEGRATION) items when the count matches exactly
      const upsellingGuestsData: typeof mainGuestsData = [];
      const integrationItems =
        bookingCart.BasketItems?.filter((item) => item.Role === "INTEGRATION") || [];

      if (integrationItems.length > 0 && guests.length > 0) {
        // Group integration items by service code + time
        const integrationGroups = new Map<string, typeof integrationItems>();
        integrationItems.forEach((item) => {
          const key = `${item.Service}-${item.StartTime}-${item.EndTime}`;
          if (!integrationGroups.has(key)) {
            integrationGroups.set(key, []);
          }
          integrationGroups.get(key)!.push(item);
        });

        // For each group, if count matches guests count, assign 1:1
        integrationGroups.forEach((items) => {
          if (items.length === guests.length) {
            items.forEach((item, idx) => {
              const guest = guests[idx];
              const guestDataTmp: GuestData[] = [
                { Code: "NAME", Content: guest.name },
                { Code: "SURNAME", Content: guest.surname },
                { Code: "GENDER", Content: guest.isFemale ? "1" : "2" },
              ];
              if (guest.email) {
                guestDataTmp.push({ Code: "EMAILADDRESS", Content: guest.email });
              }
              if (idx === 0) {
                const zipCodeToUse =
                  guest.zipCode?.trim() || referent.zipCode?.trim() || "";
                if (zipCodeToUse) {
                  guestDataTmp.push({ Code: "ADDRESS_ZIPCODE", Content: zipCodeToUse });
                }
              }
              upsellingGuestsData.push({
                PersonalData: guestDataTmp,
                RefLine: item.LineNr,
                Action: "NEW",
              });
            });
          }
        });
      }

      bookingCart.GuestsData = [...mainGuestsData, ...upsellingGuestsData];
      setBookingCart(bookingCart);
      return bookingCart;
    }
  }

  const validateVoucherGuests = () => {
    const vouchers = bookingCart?.vouchers || [];
    if (vouchers.length === 0) {
      return true;
    }

    const totalGuests = summary.totalGuests;
    const voucherPaxInvolved = vouchers.map(
      (v: any) => v.voucherData.NrPaxInvolved || 0,
    );
    const totalVoucherPax = voucherPaxInvolved.reduce(
      (sum: number, pax: number) => sum + pax,
      0,
    );

    if (totalVoucherPax === 0) {
      return true;
    }

    if (totalGuests > totalVoucherPax) {
      const applicableGuests = totalVoucherPax;
      const guestLabel = applicableGuests === 1 ? t("ospite") : t("ospiti");
      const lastPart =
        applicableGuests === 1
          ? t("Il voucher verrà applicato solo al primo ospite.")
          : t(
              "Il voucher verrà applicato solo ai primi {{applicableGuests}} ospiti.",
              { applicableGuests },
            );
      setVoucherValidationError(
        t(
          "Hai selezionato {{totalGuests}} ospiti, ma i tuoi voucher sono validi per {{applicableGuests}} {{guestLabel}}. {{lastPart}}",
          { totalGuests, applicableGuests, guestLabel, lastPart },
        ),
      );
      return true;
    }

    if (totalGuests < totalVoucherPax) {
      const totalGuestsLabel = totalGuests === 1 ? t("ospite") : t("ospiti");
      const totalVoucherPaxLabel =
        totalVoucherPax === 1 ? t("ospite") : t("ospiti");
      setVoucherValidationError(
        t(
          "Hai selezionato {{totalGuests}} {{totalGuestsLabel}}, ma i tuoi voucher sono validi per {{totalVoucherPax}} {{totalVoucherPaxLabel}}. Questo voucher non è applicabile al numero di ospiti selezionato.",
          {
            totalGuests,
            totalGuestsLabel,
            totalVoucherPax,
            totalVoucherPaxLabel,
          },
        ),
      );
      return false;
    }

    return true;
  };

  // Salva il sesso del referente sul profilo deducendolo dall'ospite che ha la
  // stessa email del referente. Best-effort (fire-and-forget) e solo se il
  // profilo non ha ancora il sesso impostato. Valori: "1" = Donna, "2" = Uomo.
  const persistReferentGenderIfNeeded = () => {
    if (!user || user.Gender) return;
    const referentEmail = (referent.email || "").trim().toLowerCase();
    if (!referentEmail) return;
    const matchingGuest = guests.find(
      (g) => (g.email || "").trim().toLowerCase() === referentEmail,
    );
    if (!matchingGuest) return;
    const gender = matchingGuest.isFemale ? "1" : "2";
    fetch("/api/user/update-gender", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ gender }),
    }).catch((error) => {
      console.error(
        "[CheckoutStep] Failed to persist referent gender:",
        error,
      );
    });
  };

  const handleConfirm = () => {
    if (isVoucherBooking) {
      setVoucherValidationError(null);
      const voucherValid = validateVoucherGuests();
      if (!voucherValid) {
        return;
      }
    }

    const formResult = validateForm();
    if (formResult) {
      mapCustomDataToRealData();
      persistReferentGenderIfNeeded();
      onConfirm();
    }
  };

  if (
    !bookingCart ||
    !bookingCart.BasketItems ||
    bookingCart.BasketItems.length === 0
  ) {
    return (
      <div className="w-full text-center">
        <h2 className="text-2xl font-bold mb-4">{t("Nessuna prenotazione")}</h2>
        <p className="text-gray-600 mb-6">
          {t("Non hai ancora selezionato nessuna esperienza")}
        </p>
        <Button variant="outline" onClick={onCancel}>
          {t("Torna alla prenotazione")}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col space-y-6 w-full max-w-4xl mx-auto px-8 py-8 relative">
      <div className="text-center">
        <h2 className="text-2xl font-bold mb-2">
          {t("Finalizza la tua prenotazione")}
        </h2>
        <p className="text-gray-600">
          {t("Inserisci i tuoi dati e procedi al pagamento")}
        </p>
      </div>

      <div className="w-full max-w-md mx-auto relative min-h-52">
        {!isDisabled && (
          <div>
            <h4 className="text-lg font-semibold mb-4">
              {t("Inserisci i dati di ogni ospite")}
            </h4>
            <div className="grid grid-cols-4 gap-2 mb-4">
              {guests.map((guest, index) => {
                const guestKey = `guest-${index}-${guest.name || ""}-${
                  guest.surname || ""
                }`;
                const isFemale = guest.isFemale ?? false;
                const genderLabel = isFemale ? t("Donna") : t("Uomo");
                const sameGenderGuests = guests.filter(
                  (g) => (g.isFemale ?? false) === isFemale,
                );
                const genderIndex =
                  sameGenderGuests.findIndex((g) => g === guest) + 1;

                return (
                  <div
                    className={cn(
                      "flex items-center justify-center h-8 w-auto px-3 min-w-16 rounded-full font-bold",
                      {
                        "bg-secondary text-white": activeGuestIndex === index,
                        "bg-gray-300 text-black cursor-pointer hover:bg-gray-400":
                          activeGuestIndex !== index && !isDisabled,
                        "opacity-50 cursor-not-allowed": isDisabled,
                      },
                    )}
                    key={guestKey}
                    onClick={() => {
                      if (isDisabled) {
                        openAuthModal("login", "booking");
                        return;
                      }
                      setActiveGuestIndex(index);
                    }}
                  >
                    <UserRound size={15} className="-mt-0.5 min-w-3.5" />
                    <span className="pl-1 whitespace-nowrap text-sm">
                      {genderLabel} {genderIndex}
                    </span>
                  </div>
                );
              })}
            </div>

            {guests.length > 0 && (
              <div>
                {canUseUserData && (
                  <Button
                    type="button"
                    variant="link"
                    onClick={() => {
                      if (isDisabled) {
                        openAuthModal("login", "booking");
                        return;
                      }
                      useReferentDataForGuest();
                    }}
                    className="mb-4 px-0"
                    disabled={isDisabled}
                  >
                    {t("Utilizza i tuoi dati")}
                  </Button>
                )}

                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label
                      htmlFor={`guest-name-${activeGuestIndex}`}
                      className="block text-sm font-medium mb-1"
                    >
                      {t("Nome")}
                    </label>
                    <Input
                      id={`guest-name-${activeGuestIndex}`}
                      name="name"
                      type="text"
                      placeholder={t("Nome")}
                      value={guests[activeGuestIndex]?.name || ""}
                      onChange={(e) =>
                        handleGuestDataChange(
                          activeGuestIndex,
                          "name",
                          e.target.value,
                        )
                      }
                      onFocus={handleInputFocus}
                      onClick={handleInputClick}
                      disabled={isDisabled}
                      required
                    />
                  </div>

                  <div>
                    <label
                      htmlFor={`guest-surname-${activeGuestIndex}`}
                      className="block text-sm font-medium mb-1"
                    >
                      {t("Cognome")}
                    </label>
                    <Input
                      id={`guest-surname-${activeGuestIndex}`}
                      name="surname"
                      type="text"
                      placeholder={t("Cognome")}
                      value={guests[activeGuestIndex]?.surname || ""}
                      onChange={(e) =>
                        handleGuestDataChange(
                          activeGuestIndex,
                          "surname",
                          e.target.value,
                        )
                      }
                      onFocus={handleInputFocus}
                      onClick={handleInputClick}
                      disabled={isDisabled}
                      required
                    />
                  </div>

                  <div>
                    <label
                      htmlFor={`guest-email-${activeGuestIndex}`}
                      className="block text-sm font-medium mb-1"
                    >
                      Email
                    </label>
                    <Input
                      id={`guest-email-${activeGuestIndex}`}
                      name="email"
                      type="email"
                      placeholder="Email"
                      value={guests[activeGuestIndex]?.email || ""}
                      onChange={(e) =>
                        handleGuestDataChange(
                          activeGuestIndex,
                          "email",
                          e.target.value,
                        )
                      }
                      onFocus={handleInputFocus}
                      onClick={handleInputClick}
                      disabled={isDisabled}
                    />
                  </div>

                  {shouldShowZipCodeForGuest1 && activeGuestIndex === 0 && (
                    <div>
                      <label
                        htmlFor={`guest-zipCode-${activeGuestIndex}`}
                        className="block text-sm font-medium mb-1"
                      >
                        {t("CAP")}
                      </label>
                      <Input
                        id={`guest-zipCode-${activeGuestIndex}`}
                        name="zipCode"
                        type="text"
                        placeholder={t("CAP")}
                        value={
                          localZipCode ||
                          guests[activeGuestIndex]?.zipCode ||
                          ""
                        }
                        onChange={(e) => {
                          const value = e.target.value;
                          setLocalZipCode(value);
                          handleGuestDataChange(
                            activeGuestIndex,
                            "zipCode",
                            value,
                          );
                        }}
                        onFocus={handleInputFocus}
                        onClick={handleInputClick}
                        disabled={isDisabled}
                        required
                        autoComplete="postal-code"
                      />
                      {errors.guests?.[activeGuestIndex]?.zipCode && (
                        <div className="text-destructive text-sm mt-1">
                          {errors.guests[activeGuestIndex].zipCode}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {errors.guests && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <div className="text-sm text-red-800 space-y-1">
                  {errors.guests
                    .flatMap((g: any) => Object.values(g).filter(Boolean))
                    .filter(Boolean)
                    .map((error: string, idx: number) => (
                      <div key={idx}>{error}</div>
                    ))}
                </div>
              </div>
            )}

            {errors.referent && Object.values(errors.referent).length > 0 && (
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="text-sm text-amber-800 space-y-1">
                  {Object.values(errors.referent)
                    .filter((error): error is string => Boolean(error))
                    .map((error: string, idx: number) => (
                      <div key={idx}>{error}</div>
                    ))}
                </div>
              </div>
            )}

            {guests.length > 0 && (
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800">
                  {t(
                    "Fai attenzione alla veridicità dei dati inseriti. Sono importanti per garantire la corretta assegnazione degli armadietti e per evitare errori durante il check-in.",
                  )}
                </p>
              </div>
            )}
          </div>
        )}
        {isDisabled && (
          <div className="absolute inset-0 bg-white border-secondary-200 border-2 rounded-xl flex flex-col items-center justify-center p-6 z-10">
            <div className="text-center space-y-4">
              <h3 className="text-xl font-semibold">
                {t("Accedi o registrati per continuare")}
              </h3>
              <p className="text-gray-600">
                {t(
                  "Per completare la prenotazione, devi effettuare il login o registrarti",
                )}
              </p>
              <Button
                variant="secondary"
                onClick={() => openAuthModal("login", "booking")}
                className="mt-4"
              >
                {t("Accedi o Registrati")}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Warning per voucher non abbinati */}
      {isVoucherBooking &&
        (() => {
          const vouchers = bookingCart?.vouchers || [];
          const voucherCodesInCart = new Set<string>(
            bookingCart?.BasketItems?.map(
              (item: any) => item.VoucherCode,
            ).filter(Boolean) || [],
          );

          const unmatchedVouchers = vouchers.filter(
            (voucher) => !voucherCodesInCart.has(voucher.code),
          );

          if (unmatchedVouchers.length === 0) return null;

          // Per ciascun voucher non abbinato verifichiamo se il servizio
          // coperto è incluso in un pacchetto già nel carrello: in quel caso
          // il motivo è specifico (il pacchetto comprende già quel servizio,
          // l'utente non se ne accorge da solo) e merita un messaggio
          // diverso da "non abbinato a nessun servizio".
          const annotated = unmatchedVouchers.map((voucher) => {
            const codes = voucher.voucherData?.ServicesIncludedCodes || [];
            const isInPackage = !!bookingCart?.BasketItems?.some(
              (item: any) =>
                codes.includes(item.Service) &&
                (item.Role === "INCLUDED" ||
                  (typeof item.RefLine === "number" && item.RefLine > 0)),
            );
            return { voucher, isInPackage };
          });

          const inPackage = annotated.filter((a) => a.isInPackage);
          const other = annotated.filter((a) => !a.isInPackage);

          return (
            <div className="w-full p-4 bg-orange-100 border border-orange-400 rounded-lg max-w-md mb-4 mx-auto">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-orange-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-orange-700 mb-1">
                    {t("Attenzione: voucher non abbinati")}
                  </p>

                  {inPackage.length > 0 && (
                    <div className="mb-2">
                      <p className="text-sm text-orange-700">
                        {t(
                          "I seguenti voucher coprono servizi già inclusi in un pacchetto, quindi non possono essere applicati:",
                        )}
                      </p>
                      <ul className="list-disc list-inside mt-1 text-sm text-orange-700">
                        {inPackage.map(({ voucher }) => (
                          <li key={voucher.code} className="font-mono">
                            {voucher.code}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {other.length > 0 && (
                    <div>
                      <p className="text-sm text-orange-700">
                        {t(
                          "I seguenti voucher non sono stati abbinati a nessun servizio nel carrello e non verranno utilizzati:",
                        )}
                      </p>
                      <ul className="list-disc list-inside mt-1 text-sm text-orange-700">
                        {other.map(({ voucher }) => (
                          <li key={voucher.code} className="font-mono">
                            {voucher.code}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })()}
      {isVoucherBooking && voucherValidationError && (
        <div className="w-full p-4 bg-amber-50 border border-amber-200 rounded-lg max-w-md mx-auto">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-amber-800">{voucherValidationError}</p>
          </div>
        </div>
      )}
      <div className="flex flex-row items-center justify-center space-x-2">
        <Button
          variant={"muted"}
          onClick={() => {
            onCancel();
          }}
        >
          <span>{t("Indietro")}</span>
        </Button>

        <Button
          type="button"
          variant="secondary"
          onClick={handleConfirm}
          className={cn({
            "opacity-50 cursor-not-allowed": disableConfirm || isDisabled,
          })}
          disabled={
            isDisabled ||
            disableConfirm ||
            isSubmitting ||
            loadingRemove ||
            (!user && !hasBookingCustomerToken) ||
            (voucherValidationError !== null &&
              voucherValidationError.includes("non è applicabile"))
          }
        >
          {isSubmitting || loadingRemove ? (
            <div className="flex items-center space-x-2">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-solid border-white border-t-transparent"></div>
              <span>
                {loadingRemove ? t("Eliminazione...") : t("Elaborazione...")}
              </span>
            </div>
          ) : (
            t("Procedi al pagamento")
          )}
        </Button>
      </div>

      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {isMainServiceToDelete
                ? t("Elimina servizio principale")
                : t("Elimina servizio aggiuntivo")}
            </DialogTitle>
            <DialogDescription>
              {isMainServiceToDelete ? (
                <span>
                  {t("Sei sicuro di voler eliminare questo servizio?")}
                  <br />
                  {t(
                    "Questa azione ti riporterà all'inizio del flusso di prenotazione e svuoterà il carrello.",
                  )}
                </span>
              ) : (
                <span>
                  {t(
                    "Sei sicuro di voler eliminare questo servizio aggiuntivo?",
                  )}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="muted"
              onClick={() => {
                setDeleteConfirmOpen(false);
                setItemToDelete(null);
              }}
            >
              {t("Annulla")}
            </Button>
            <Button
              variant="secondary"
              onClick={confirmDelete}
              disabled={loadingRemove}
            >
              {loadingRemove ? t("Eliminazione...") : t("Elimina")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
